/**
 * "Opin lög": the strip of open songs and lists. It floats over the sheet like the edit toolbar —
 * the sheet keeps the whole height under the top bar — docked along the top (by default at the
 * left, clear of the page indicator) or the bottom; drag its grip or use the grip's arrow keys
 * (lib/useFloatingDock), and it can be folded down to the open tab alone (« / »). Both are remembered
 * per device. It reports what it covers (onInsets) so the sheet's chips keep clear of it. Shown when
 * it helps: two or more tabs, a list's tab, or a song with several sheets (on the not-found / error
 * page, which has no tab of its own: any tab).
 *
 *  - A song tab: title, and the sheet showing (▾ on the active tab when there are several sheets:
 *    tapping it opens "Velja nótur"). A list tab: the list's name, and "3/12 · song title" (▾:
 *    tapping the active one opens the list drawer, which also holds the sheet choice).
 *  - Tapping another tab switches to it in place (history replace, so Back still leaves the song
 *    page), restoring its sheet and page. New tabs come in on the right; switching never reorders.
 *  - × only on the active tab (fewer accidental closes): the tab to its right opens, else the one to
 *    its left, else the page goes back like the Back button. "+" opens the library's lists.
 *  - Not shown while writing (Skrifa is full screen: the song page hides its top bar and the strip).
 *  - Strict visibility: only songs / lists this user can see right now (in the song / list caches,
 *    which also work offline); the rest stay hidden.
 *
 * Buttons are never nested (a tab is a <button> plus a sibling × button); every target ≥ 44 pt.
 */
import { useQueryClient } from '@tanstack/react-query';
import { CalendarCheck, ChevronDown, ChevronsLeft, ChevronsRight, Folder, GripVertical, ListOrdered, Music, Plus, X, type LucideIcon } from 'lucide-react';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import type { CollectionDTO, SongDTO } from '../../shared/dto/library';
import { practiceEventId } from '../../shared/program';
import { collectionKey, songKey, useCollections, useSongs } from '../api/library';
import { isQueryKeyGone } from '../api/queryClient';
import { cx } from '../components/ui';
import { useT } from '../i18n';
import { DOCK_MARGIN, type Dock, type DockEdge } from '../lib/dock';
import { useFloatingDock, type DockInsets } from '../lib/useFloatingDock';
import { orderedEntries } from '../viewer/setlist';
import { close, hrefFor, type Tab, type TabKey } from './model';
import { SheetMenu, type SheetChoice } from './SheetMenu';
import { updateTabs, useTabsState } from './store';
import { TAB_NAV_STATE, type TabSync } from './useTabSync';

interface TabView {
  tab: Tab;
  active: boolean;
  icon: LucideIcon;
  /** Line 1: song title / list name. */
  title: string;
  /** Line 2: the sheet / "3/12 · song title". */
  detail: string | null;
  /** Accessible name of the tab button. */
  label: string;
  href: string;
}

/** The tabs this user can see now, with what they show (from the query caches; offline too). */
function useTabViews(items: readonly Tab[], activeKey: TabKey | null, active: SheetChoice | null): TabView[] {
  const t = useT();
  const qc = useQueryClient();
  const songsQ = useSongs();
  const colsQ = useCollections();
  const version = useCacheVersion();
  return useMemo(() => {
    const songs = new Map((songsQ.data ?? []).map((s) => [s.id, s]));
    const lists = new Map((colsQ.data ?? []).map((c) => [c.id, c]));
    const cachedSong = (id: string) => (isQueryKeyGone(qc, songKey(id)) ? undefined : qc.getQueryData<SongDTO>(songKey(id)));
    const songTitle = (id: string) => songs.get(id)?.title ?? cachedSong(id)?.title ?? null;
    const sheetTitle = (songId: string, sheetId: string | null) => (sheetId ? (cachedSong(songId)?.files.find((f) => f.id === sheetId)?.title ?? null) : null);

    const out: TabView[] = [];
    for (const tab of items) {
      const isActive = tab.key === activeKey;
      if (tab.kind === 'song') {
        const title = songTitle(tab.songId);
        if (title === null) continue; // not visible to this user (now)
        const sheet = isActive && active ? (active.files.find((f) => f.id === active.selectedId)?.title ?? null) : sheetTitle(tab.songId, tab.sheetId);
        out.push({
          tab,
          active: isActive,
          icon: Music,
          title,
          detail: sheet,
          label: sheet ? t('tabs.song_aria', { title, sheet }) : title,
          href: hrefFor(tab, null),
        });
        continue;
      }
      // A list: in the list of lists (before that has loaded: its own cached copy, offline too). A
      // practice is never in it: its own cached copy only.
      const gone = isQueryKeyGone(qc, collectionKey(tab.collectionId));
      const cached = gone ? undefined : qc.getQueryData<CollectionDTO>(collectionKey(tab.collectionId));
      const practice = !!practiceEventId(tab.collectionId);
      const listed = practice ? undefined : lists.get(tab.collectionId);
      if (practice || !colsQ.data ? !cached : !listed) continue;
      const col = (cached ?? listed)!;
      const ordered = orderedEntries(col.entries);
      const k = tab.entryId ? ordered.findIndex((e) => e.entryId === tab.entryId) : -1;
      const entry = k >= 0 ? ordered[k] : undefined;
      const current = entry ? songTitle(entry.songId) : null;
      const position = entry ? t('tabs.list_position', { position: k + 1, total: ordered.length }) : null;
      out.push({
        tab,
        active: isActive,
        icon: practice ? CalendarCheck : col.kind === 'folder' ? Folder : ListOrdered,
        title: col.name,
        detail: position ? (current ? `${position} · ${current}` : position) : null,
        label: entry ? t('tabs.collection_aria', { name: col.name, position: k + 1, total: ordered.length }) : col.name,
        href: hrefFor(tab, col.entries),
      });
    }
    return out;
    // `version`: a single song / list changed in the cache (a poll, a reorder) — they are read above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, activeKey, active, songsQ.data, colsQ.data, qc, t, version]);
}

/** Bumped when a single song or list in the query cache got new data or went away. */
function useCacheVersion(): number {
  const qc = useQueryClient();
  const [version, setVersion] = useState(0);
  useEffect(
    () =>
      qc.getQueryCache().subscribe((e) => {
        const root = e.query.queryKey[0];
        if (root !== 'song' && root !== 'collection') return;
        if (e.type === 'removed' || (e.type === 'updated' && (e.action.type === 'success' || e.action.type === 'error' || e.action.type === 'setState'))) {
          // After the current task: a cache write may happen while another component renders.
          queueMicrotask(() => setVersion((v) => v + 1));
        }
      }),
    [qc],
  );
  return version;
}

/** Horizontal scroll position → which edges fade (only where more tabs hide). `shown`: the list is mounted. */
function useEdgeFade(shown: boolean) {
  const ref = useRef<HTMLUListElement>(null);
  const [fade, setFade] = useState({ left: false, right: false });
  const update = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const left = el.scrollLeft > 1;
    const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
    setFade((f) => (f.left === left && f.right === right ? f : { left, right }));
  }, []);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    update();
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update);
    ro?.observe(el);
    return () => ro?.disconnect();
  }, [update, shown]);
  const mask =
    fade.left || fade.right
      ? `linear-gradient(to right, ${fade.left ? 'transparent' : '#000'}, #000 28px, #000 calc(100% - 28px), ${fade.right ? 'transparent' : '#000'})`
      : undefined;
  return { ref, update, style: mask ? { maskImage: mask, WebkitMaskImage: mask } : undefined };
}

export const TABS_DOCK_STORAGE = 'kgr.tabs.dock';
const TABS_FOLDED_STORAGE = 'kgr.tabs.folded';
const TABS_EDGES: readonly DockEdge[] = ['top', 'bottom'];
/** Top left: the page indicator sits top right. */
const TABS_HOME: Dock = { edge: 'top', along: 0 };
/** Room the strip leaves for the viewer's page indicator (top right) when it lies along the top. */
const INDICATOR_ROOM_PX = 104;

function readFolded(): boolean {
  try {
    return localStorage.getItem(TABS_FOLDED_STORAGE) === '1';
  } catch {
    return false;
  }
}

function writeFolded(v: boolean): void {
  try {
    if (v) localStorage.setItem(TABS_FOLDED_STORAGE, '1');
    else localStorage.removeItem(TABS_FOLDED_STORAGE);
  } catch {
    /* storage unavailable – lasts for this session only */
  }
}

export function TabStrip({
  sync,
  sheets = null,
  sheetMenu: menuOffered = true,
  onOpenList,
  onBack,
  message = false,
  onInsets,
}: {
  sync: TabSync;
  /** The open song's sheets (more than one: the strip shows, and the active tab opens "Velja nótur"). */
  sheets?: SheetChoice | null;
  /** The sheet menu is offered (the Nótur tab; on Texti the tab only names the sheet). */
  sheetMenu?: boolean;
  /** Opens the list drawer (tapping the active list tab). */
  onOpenList?: () => void;
  /** Leaves the song page (× on the last tab). */
  onBack: () => void;
  /** A not-found / offline / error page: any visible tab is a way out. */
  message?: boolean;
  /** What the floating strip covers of its parent (0 everywhere while it is hidden). */
  onInsets?: (insets: DockInsets) => void;
}) {
  const t = useT();
  const navigate = useNavigate();
  const floating = useFloatingDock<HTMLElement>({
    storageKey: TABS_DOCK_STORAGE,
    fallback: TABS_HOME,
    edges: TABS_EDGES,
    maxWidth: (w, edge) => w - 2 * DOCK_MARGIN - (edge === 'top' ? INDICATOR_ROOM_PX : 0),
  });
  const { top, bottom } = floating.insets;
  useEffect(() => {
    onInsets?.({ top, bottom, left: 0, right: 0 });
  }, [onInsets, top, bottom]);
  const [folded, setFoldedState] = useState(readFolded);
  const setFolded = (v: boolean) => {
    setFoldedState(v);
    writeFolded(v);
  };
  const { userId, doc, loaded } = useTabsState();
  const mine = loaded && doc && userId === sync.userId ? doc : null;
  const views = useTabViews(mine?.items ?? [], sync.activeKey, sheets);
  const [sheetMenu, setSheetMenu] = useState(false);

  const activeView = views.find((v) => v.active);
  // Several sheets keep the strip on both Nótur and Texti (switching them never resizes the sheet).
  const severalSheets = !!sheets && sheets.files.length > 1;
  const multiSheet = severalSheets && menuOffered;
  // On a song that is gone the page has no tab of its own, so one other tab already helps.
  const show = !!mine && (views.length >= (message && !activeView ? 1 : 2) || activeView?.tab.kind === 'collection' || severalSheets);
  // Folded: only the open tab (a page that has none shows them all).
  const foldable = !!activeView && views.length > 1;
  const isFolded = folded && foldable;
  const shownViews = isFolded ? [activeView!] : views;
  const fade = useEdgeFade(show);

  // The active tab in view (scrollLeft, not scrollIntoView: that would scroll the page as well).
  const activeKey = activeView?.tab.key ?? null;
  useLayoutEffect(() => {
    const el = fade.ref.current;
    const tab = el?.querySelector<HTMLElement>('[data-active-tab]');
    if (!el || !tab) return;
    const pad = 28;
    if (tab.offsetLeft - pad < el.scrollLeft) el.scrollLeft = Math.max(0, tab.offsetLeft - pad);
    else if (tab.offsetLeft + tab.offsetWidth + pad > el.scrollLeft + el.clientWidth) el.scrollLeft = tab.offsetLeft + tab.offsetWidth + pad - el.clientWidth;
    fade.update();
  }, [activeKey, show, views.length, isFolded, fade.ref, fade.update]);

  if (!show || !mine) return null;

  const visibleKeys = new Set(views.map((v) => v.tab.key));

  const onTab = (v: TabView) => {
    if (!v.active) {
      navigate(v.href, { replace: true, state: TAB_NAV_STATE });
      return;
    }
    // The active list tab: its drawer (songs + sheet choice).
    if (v.tab.kind === 'collection' && onOpenList) onOpenList();
    else if (multiSheet) setSheetMenu(true);
  };

  const onClose = (v: TabView) => {
    const { next } = close(mine, v.tab.key, (x) => visibleKeys.has(x.key));
    updateTabs(sync.userId, (d) => close(d, v.tab.key).doc);
    const to = next ? views.find((x) => x.tab.key === next.key) : undefined;
    if (to) navigate(to.href, { replace: true, state: TAB_NAV_STATE });
    else onBack();
  };

  const onMore = () => navigate('/songs?tab=setlists');

  return (
    <nav
      ref={floating.ref}
      aria-label={t('tabs.label')}
      data-dock={floating.dock.edge}
      data-folded={isFolded ? '' : undefined}
      className="kgr-tabs pointer-events-auto absolute top-0 left-0 z-30 flex items-center gap-0.5 rounded-2xl bg-[var(--surface)] p-1 shadow-[0_6px_24px_rgb(0_0_0/0.22)] ring-1 ring-[var(--line)]"
      style={floating.style}
    >
      <button
        type="button"
        aria-label={t('tabs.move')}
        title={t('tabs.move_hint')}
        {...floating.gripProps}
        className={cx(
          'flex h-12 w-11 shrink-0 cursor-grab touch-none items-center justify-center rounded-xl text-[var(--muted)] hover:bg-[var(--surface-2)] active:cursor-grabbing',
          floating.dragging && 'bg-[var(--accent-soft)] text-[var(--accent)]',
        )}
      >
        <GripVertical className="size-6" aria-hidden />
      </button>
      <ul ref={fade.ref} onScroll={fade.update} className="relative flex min-w-0 items-center gap-1 overflow-x-auto" style={{ scrollbarWidth: 'none', ...fade.style }}>
        {shownViews.map((v) => {
          const caret = v.active && (multiSheet || v.tab.kind === 'collection');
          return (
            <li
              key={v.tab.key}
              data-tab={v.tab.key}
              data-active-tab={v.active ? '' : undefined}
              className={cx('flex shrink-0 items-center rounded-xl', v.active && 'bg-[var(--accent-soft)] ring-1 ring-[var(--accent)]/35')}
            >
              <button
                type="button"
                aria-current={v.active ? 'page' : undefined}
                aria-haspopup={v.active && caret ? 'dialog' : undefined}
                aria-label={v.label}
                onClick={() => onTab(v)}
                className={cx(
                  'flex min-h-12 min-w-11 items-center gap-2 rounded-xl py-1 text-left transition',
                  // pr-3: the tab's own area runs past its ▾ before the × begins (no near-miss close).
                  v.active ? 'pr-3 pl-3 text-[var(--accent)]' : 'px-3 text-[var(--ink-2)] hover:bg-[var(--accent-soft)]',
                )}
              >
                <v.icon className="size-5 shrink-0" aria-hidden />
                <span className="flex min-w-0 flex-col" aria-hidden>
                  <span className={cx('truncate text-[16px] leading-tight font-semibold', v.active ? 'max-w-[16rem]' : 'max-w-[11rem]')}>{v.title}</span>
                  {v.detail || caret ? (
                    <span className={cx('flex items-center gap-0.5 text-[14px] leading-tight', v.active ? 'text-[var(--ink-2)]' : 'text-[var(--muted)]')}>
                      <span className={cx('truncate', v.active ? 'max-w-[15rem]' : 'max-w-[11rem]')}>{v.detail}</span>
                      {caret ? <ChevronDown className="size-4 shrink-0" aria-hidden /> : null}
                    </span>
                  ) : null}
                </span>
              </button>
              {v.active ? (
                <button
                  type="button"
                  aria-label={t('tabs.close', { title: v.title })}
                  title={t('tabs.close', { title: v.title })}
                  onClick={() => onClose(v)}
                  className="mr-0.5 inline-flex size-11 shrink-0 items-center justify-center rounded-full text-[var(--ink-2)] transition hover:bg-[var(--surface)]"
                >
                  <X className="size-5" aria-hidden />
                </button>
              ) : null}
            </li>
          );
        })}
      </ul>
      {isFolded ? null : (
        <button
          type="button"
          aria-label={t('tabs.open_more')}
          title={t('tabs.open_more')}
          onClick={onMore}
          className="inline-flex size-11 shrink-0 items-center justify-center rounded-xl text-[var(--accent)] transition hover:bg-[var(--accent-soft)]"
        >
          <Plus className="size-6" aria-hidden />
        </button>
      )}
      {foldable ? (
        <button
          type="button"
          aria-expanded={!isFolded}
          aria-label={isFolded ? t('tabs.unfold', { count: views.length - 1 }) : t('tabs.fold')}
          title={isFolded ? t('tabs.unfold', { count: views.length - 1 }) : t('tabs.fold')}
          onClick={() => setFolded(!isFolded)}
          className="inline-flex h-11 min-w-11 shrink-0 items-center justify-center gap-0.5 rounded-xl px-1.5 text-[15px] font-semibold text-[var(--ink-2)] transition hover:bg-[var(--surface-2)]"
        >
          {isFolded ? (
            <>
              <span className="tabular-nums" aria-hidden>
                +{views.length - 1}
              </span>
              <ChevronsRight className="size-5" aria-hidden />
            </>
          ) : (
            <ChevronsLeft className="size-5" aria-hidden />
          )}
        </button>
      ) : null}
      <SheetMenu open={sheetMenu && multiSheet} onClose={() => setSheetMenu(false)} choice={sheets} />
    </nav>
  );
}
